// Sidebar.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, Snackbar, Button } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import DispatchIcon from '@mui/icons-material/MinorCrash';
import FutureIcon from '@mui/icons-material/Update';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChatIcon from '@mui/icons-material/Chat';
import ChatDialog from './Dispatch/Itinerary/ChatDialog';

function Sidebar({
  open,
  onClose,
  activeComponent,
  userSettings: propUserSettings,
  fleetSettings: propFleetSettings,
  navigateToComponent,
  dispatchType,
  socket,
  selectedVehicle,
  routeDetails,
}) {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatNotification, setChatNotification] = useState(null);

  // Tracks the timestamp after which any dispatch message is considered "new".
  // Session-only (useRef) so we never re-notify about messages from before this session.
  const lastSeenTimestampRef = useRef(Date.now());

  // Poll chat:getMessages for messages that arrived while the app was backgrounded.
  // Called on socket reconnect and when the page regains visibility.
  const checkForMissedMessages = useCallback(() => {
    if (!socket || !routeDetails?.routeId || chatOpen) return;

    socket.emit('chat:getMessages', { routeId: routeDetails.routeId }, (response) => {
      if (!Array.isArray(response)) return;

      const cutoff = lastSeenTimestampRef.current;
      lastSeenTimestampRef.current = Date.now();

      // Find dispatch messages (direction 'Out') newer than the last seen timestamp
      const missed = response.filter(msg => {
        if (msg.direction !== 'Out') return false;
        const ts = msg.timestamp ? new Date(msg.timestamp).getTime() : 0;
        return ts > cutoff;
      });

      if (missed.length === 0) return;

      // Surface the most recent missed message
      const latest = missed.sort((a, b) =>
        new Date(b.timestamp) - new Date(a.timestamp)
      )[0];

      setChatNotification({
        sender: latest.from || 'Dispatch',
        message: latest.message || latest.text || latest.data || ''
      });
    });
  }, [socket, routeDetails?.routeId, chatOpen]);

  // Trigger a missed-message check whenever the socket reconnects (app was backgrounded).
  useEffect(() => {
    if (!socket) return;
    socket.on('connect', checkForMissedMessages);
    return () => {
      socket.off('connect', checkForMissedMessages);
    };
  }, [socket, checkForMissedMessages]);

  // Trigger a missed-message check whenever the page becomes visible again.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForMissedMessages();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForMissedMessages]);

  // Listen for incoming chat messages when chat dialog is closed
  useEffect(() => {
    if (!socket || !routeDetails?.routeId) return;

    const handleNewMessage = (messageData) => {
      // Only show notification if chat dialog is closed and it's for our route
      if (!chatOpen && messageData.routeId === routeDetails.routeId) {
        setChatNotification({
          sender: messageData.senderName || 'Dispatch',
          message: messageData.message || ''
        });
      }
    };

    socket.on('chat:newMessage', handleNewMessage);

    return () => {
      socket.off('chat:newMessage', handleNewMessage);
    };
  }, [socket, routeDetails?.routeId, chatOpen]);

  const handleCloseNotification = useCallback(() => {
    setChatNotification(null);
  }, []);

  const handleOpenChat = useCallback(() => {
    setChatNotification(null);
    lastSeenTimestampRef.current = Date.now();
    setChatOpen(true);
  }, []);

  const handleItemClick = (componentName) => {
    if (componentName === 'chat') {
      // Open chat dialog instead of navigating
      setChatOpen(true);
      onClose();
      return;
    }
    if (componentName === 'sos') {
      // Open the emergency confirm step (EmergencyAlarm listens for this).
      window.dispatchEvent(new CustomEvent('driverapp:open-sos'));
      onClose();
      return;
    }
    if (navigateToComponent) {
      navigateToComponent(componentName);
    }
    onClose();
  };

  // Show chat for itinerary, looped shuttle, and fixed shuttle dispatch modes
  const showChat = dispatchType === 'itinerary' ||
                   dispatchType === 'shuttlebus-looped' ||
                   dispatchType === 'shuttlebus-fixed';

  const menuItems = [
    {
      component: 'dashboard',
      text: 'Home',
      icon: <HomeIcon />,
      permission: true // Always shown — entry point to the dashboard home
    },
    {
      component: 'Dashboard/Future',
      text: 'Schedule',
      icon: <FutureIcon />,
      permission: propFleetSettings?.future
    },
    {
      component: 'dispatch',
      text: 'Dispatch',
      icon: <DispatchIcon />,
      permission: true // Always shown
    },
    {
      component: 'chat',
      text: 'Chat with Dispatch',
      icon: <ChatIcon />,
      permission: showChat // Only shown in itinerary / shuttle modes
    },
    {
      component: 'Dispatch/CompleteShift',
      text: 'End Shift',
      icon: <ExitToAppOutlinedIcon />,
      permission: true // Always shown
    },
    {
      component: 'logout',
      text: 'Logout',
      icon: <LogoutIcon />,
      permission: true // Always shown — logs out and returns to the driver login page
    },
    {
      component: 'sos',
      text: 'Emergency SOS',
      icon: <WarningAmberIcon />,
      permission: true, // Always shown — opens the emergency alert confirm step
      danger: true
    }
  ];

  return (
    <>
      <Drawer anchor="left" open={open} onClose={onClose}>
        <Typography variant="h6" align="center" gutterBottom sx={{ mt: 2 }}>
          {propUserSettings?.lastName}, {propUserSettings?.firstName}
        </Typography>
        <Divider />

        <List>
          {menuItems.map((item, index) => (
            item.permission && (
              <ListItemButton
                key={index}
                onClick={() => handleItemClick(item.component)}
                selected={activeComponent === item.component}
                sx={item.danger ? { color: 'error.main' } : undefined}
              >
                <ListItemIcon sx={item.danger ? { color: 'error.main' } : undefined}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={item.danger ? { fontWeight: 700 } : undefined}
                />
              </ListItemButton>
            )
          ))}
        </List>
      </Drawer>

      {/* Chat Dialog for itinerary / shuttle modes */}
      <ChatDialog
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        socket={socket}
        routeId={routeDetails?.routeId}
        driverName={propUserSettings ? `${propUserSettings.firstName} ${propUserSettings.lastName}` : 'Driver'}
      />

      {/* Chat notification snackbar */}
      <Snackbar
        open={!!chatNotification}
        onClose={handleCloseNotification}
        message={chatNotification ? `${chatNotification.sender}: ${chatNotification.message}` : ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            fontSize: '1.5rem',
            padding: '16px 24px',
            '& .MuiSnackbarContent-message': {
              fontSize: '1.5rem',
              padding: '8px 0'
            },
            '& .MuiSnackbarContent-action': {
              paddingLeft: '24px'
            }
          }
        }}
        action={
          <>
            <Button
              color="primary"
              onClick={handleOpenChat}
              sx={{ fontSize: '1.25rem', padding: '8px 16px' }}
            >
              Open
            </Button>
            <Button
              color="inherit"
              onClick={handleCloseNotification}
              sx={{ fontSize: '1.25rem', padding: '8px 16px' }}
            >
              Dismiss
            </Button>
          </>
        }
      />
    </>
  );
}

export default Sidebar;
